// FILE: src/services/imageUploadService.js
//
// Phase 3.5 — Supabase Storage uploads for visual_interpretation exhibits.
//
// ── Public API ──────────────────────────────────────────────────────────────
//
//   uploadQuestionImage(file, { examId, questionId })  →  Promise<{ publicUrl, path }>
//
// ── Behaviour ───────────────────────────────────────────────────────────────
//
//   1. Validates the file client-side (type, size).  Rejects with an
//      UploadValidationError carrying a user-presentable message.
//   2. Uploads to the `question-images` bucket under
//        <safeExamId>/<safeQuestionId>-<timestamp>.<ext>
//      so the path is exam-scoped, traceable, and never collides.
//   3. Returns the public URL (suitable for image.src) plus the storage path
//      (useful if a future admin tool wants to delete or re-upload).
//
// ── Why client-side validation when the bucket already enforces it? ─────────
//
//   Two reasons:
//
//     • Faster feedback — we don't make the user wait for an HTTP round-trip
//       to know they uploaded a 12 MB photo.
//     • Better error message — the storage error response is opaque
//       ("invalid_request"); our message tells them exactly what to fix.
//
//   The bucket constraints stay as the authoritative defence in depth.

import { supabase } from './supabaseClient';

export const QUESTION_IMAGES_BUCKET = 'question-images';

// Mirrors the bucket constraints in migration 014.
export const MAX_BYTES = 5 * 1024 * 1024;  // 5 MB
export const ALLOWED_MIME_TYPES = Object.freeze([
  'image/png',
  'image/jpeg',
  'image/webp',
  'image/svg+xml',
]);

// Maps an accepted MIME type to the file extension we store under.
const MIME_TO_EXT = Object.freeze({
  'image/png':     'png',
  'image/jpeg':    'jpg',
  'image/webp':    'webp',
  'image/svg+xml': 'svg',
});

/**
 * Thrown by uploadQuestionImage when the supplied file fails client-side
 * validation.  Carries a user-presentable `message`.
 */
export class UploadValidationError extends Error {
  constructor(message) {
    super(message);
    this.name = 'UploadValidationError';
  }
}

/**
 * Sanitizes an arbitrary string into a safe path segment.  Lowercased,
 * non-alphanumeric / non-dash / non-underscore characters replaced with `_`.
 */
function safeSegment(raw, fallback) {
  const s = String(raw ?? '').replace(/[^a-z0-9_-]/gi, '_').toLowerCase();
  return s || fallback;
}

/**
 * Uploads an image file to the question-images Storage bucket and returns
 * its public URL plus the storage path.
 *
 * @param {File}   file
 * @param {object} ctx
 * @param {string} [ctx.examId]     - 'pmp' | 'cpmai' | other.  Defaults to 'misc'.
 * @param {string} [ctx.questionId] - existing question id for traceability.
 *                                    Defaults to 'new' for not-yet-saved questions.
 * @returns {Promise<{ publicUrl: string, path: string }>}
 * @throws  {UploadValidationError} on client-side validation failures
 * @throws  {Error}                 on Supabase / network failures
 */
export async function uploadQuestionImage(file, { examId, questionId } = {}) {
  if (!file) {
    throw new UploadValidationError('No file selected.');
  }
  if (!ALLOWED_MIME_TYPES.includes(file.type)) {
    throw new UploadValidationError(
      `Unsupported file type "${file.type || 'unknown'}". Allowed: PNG, JPG, WebP, SVG.`,
    );
  }
  if (file.size > MAX_BYTES) {
    const mb = (file.size / 1024 / 1024).toFixed(1);
    throw new UploadValidationError(
      `File is too large (${mb} MB). Maximum is 5 MB.`,
    );
  }

  const safeExamId     = safeSegment(examId, 'misc');
  const safeQuestionId = safeSegment(questionId, 'new');
  const ext            = MIME_TO_EXT[file.type] || 'bin';
  const timestamp      = Date.now();
  const path           = `${safeExamId}/${safeQuestionId}-${timestamp}.${ext}`;

  const { error: uploadError } = await supabase.storage
    .from(QUESTION_IMAGES_BUCKET)
    .upload(path, file, {
      cacheControl: '31536000',  // 1 year — files are content-addressed by timestamp
      upsert: false,
      contentType: file.type,
    });

  if (uploadError) {
    throw new Error(`Upload failed: ${uploadError.message || 'unknown error'}`);
  }

  // getPublicUrl never throws and is synchronous on supabase-js v2.
  const { data } = supabase.storage
    .from(QUESTION_IMAGES_BUCKET)
    .getPublicUrl(path);

  if (!data || !data.publicUrl) {
    throw new Error('Upload succeeded but the public URL could not be resolved.');
  }

  return { publicUrl: data.publicUrl, path };
}

/**
 * Convenience helper for tests — returns the static bucket constraints so
 * test fixtures don't have to duplicate the hard-coded values.
 */
export function getUploadConstraints() {
  return {
    bucket:           QUESTION_IMAGES_BUCKET,
    maxBytes:         MAX_BYTES,
    allowedMimeTypes: [...ALLOWED_MIME_TYPES],
  };
}
